import { Cable as CablePlus, Copy, Move, Navigation, Plus, Route, Scissors, Siren, Trash2, Zap } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ApiError, api, type EndpointRef, type EndpointView, type NodeDetail, type PortView, type SplitterView } from '../api'
import { useApp } from '../context'
import { NODE_KIND_LABEL, budgetVerdict, fmtAge, fmtDbm } from '../format'
import { useAsync } from '../hooks'
import { PanelShell } from './PanelShell'
import { Badge, Button, Callout, Empty, ErrorState, FiberSwatch, KeyValue, Loading, Section, Tabs, cx } from './ui'

type Tab = 'overview' | 'ports' | 'cores' | 'splices'

export function NodePanel({ id }: { id: string }) {
  const { open, setHighlight, focusOn, incidents, reloadIncidents, canEdit, edit, setEdit, refreshNetwork, back, notify } = useApp()
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [actionBusy, setActionBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const q = useAsync(() => api.node(id), [id])
  const demoPower = async () => {
    await api.demoAlarm({ kind: 'power', nodeId: id })
    reloadIncidents()
    open({ kind: 'incidents' })
  }
  const [tab, setTab] = useState<Tab>('overview')
  const [splicing, setSplicing] = useState<EndpointRef | null | undefined>(undefined) // undefined = form closed

  useEffect(() => {
    setHighlight({})
    setSplicing(undefined)
  }, [id, setHighlight])
  useEffect(() => {
    if (q.data) {
      const hasPorts = q.data.splitters.some((s) => s.ports.some((p) => p.state === 'subscriber'))
      setTab(hasPorts ? 'ports' : 'overview')
    }
    // Only when the node changes, not on every reload after a splice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data?.node.id])

  if (!q.data) return q.error ? <ErrorState error={q.error} onRetry={q.reload} /> : <Loading />
  const d = q.data
  const n = d.node
  const subscribers = d.splitters.reduce((s, x) => s + x.ports.filter((p) => p.state === 'subscriber').length, 0)
  const outputsUsed = d.splitters.reduce((s, x) => s + x.ports.filter((p) => p.state !== 'free').length, 0)
  const ports = d.splitters.reduce((s, x) => s + x.ratio, 0)
  const [lng, lat] = n.location

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    ...(d.splitters.length ? [{ id: 'ports' as const, label: 'Ports', count: ports }] : []),
    { id: 'cores', label: 'Cores', count: d.cables.length },
    { id: 'splices', label: 'Splices', count: d.connections.length },
  ]

  return (
    <PanelShell
      title={n.name}
      subtitle={NODE_KIND_LABEL[n.kind]}
      badge={<Badge tone={n.status === 'active' ? 'ok' : n.status === 'faulty' ? 'danger' : 'neutral'}>{n.status}</Badge>}
      actions={
        <>
          <a
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-surface-2 px-2.5 text-[13px] font-medium hover:bg-surface"
            href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`}
            target="_blank"
            rel="noreferrer"
          >
            <Navigation className="h-4 w-4" aria-hidden /> Directions
          </a>
          <Button size="sm" onClick={() => focusOn(n.location, 18)}>
            <Route className="h-4 w-4" aria-hidden /> Show on map
          </Button>
          <Button size="sm" onClick={() => open({ kind: 'impact', target: { kind: 'node', nodeId: n.id } })}>
            <Zap className="h-4 w-4" aria-hidden /> Simulate failure
          </Button>
          {canEdit && (
            <Button
              size="sm"
              onClick={() => {
                setEdit({ kind: 'cable', fromNodeId: n.id, via: [] })
                open({ kind: 'add-cable' })
              }}
            >
              <CablePlus className="h-4 w-4" aria-hidden /> Draw cable from here
            </Button>
          )}
          {canEdit && n.kind !== 'pop' && (
            <>
              <Button size="sm" onClick={() => setEdit({ kind: 'move', nodeId: n.id })}>
                <Move className="h-4 w-4" aria-hidden /> Move
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmRemove(true)} aria-label={`Remove ${n.name}`}>
                <Trash2 className="h-4 w-4" aria-hidden /> Remove
              </Button>
            </>
          )}
          {incidents?.synthetic && canEdit && n.kind !== 'pop' && (
            <Button size="sm" variant="ghost" onClick={demoPower} title="Raises demo dying-gasp alarms for ONUs behind this node. Nothing is sent to any OLT.">
              <Siren className="h-4 w-4" aria-hidden /> Demo: power outage here
            </Button>
          )}
        </>
      }
    >
      {edit.kind === 'move' && edit.nodeId === n.id && (
        <div className="border-b border-border bg-accent-soft p-3 text-sm" role="status">
          {edit.to ? (
            <>
              <p>
                Move <strong>{n.name}</strong> to {edit.to[1].toFixed(6)}, {edit.to[0].toFixed(6)}? Cable ends attached to it move too.
              </p>
              <div className="mt-2 flex gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  busy={actionBusy}
                  onClick={async () => {
                    setActionBusy(true)
                    setActionError(null)
                    try {
                      await api.moveNode(n.id, edit.to!, n.revision)
                      setEdit({ kind: 'none' })
                      refreshNetwork()
                      q.reload()
                      notify(`Moved ${n.name}`)
                    } catch (e) {
                      setActionError(e instanceof Error ? e.message : 'Could not move')
                    } finally {
                      setActionBusy(false)
                    }
                  }}
                >
                  Move here
                </Button>
                <Button size="sm" onClick={() => setEdit({ kind: 'move', nodeId: n.id })}>
                  Pick again
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEdit({ kind: 'none' })}>
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <p>Tap the new position on the map. Esc cancels.</p>
          )}
        </div>
      )}
      {confirmRemove && (
        <div className="border-b border-border bg-danger-soft p-3 text-sm" role="alertdialog" aria-label={`Remove ${n.name}`}>
          <p>
            Remove <strong>{n.name}</strong> from the records? Only possible when no cables, splices or subscribers are attached. This is recorded in the audit log.
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              variant="danger"
              busy={actionBusy}
              onClick={async () => {
                setActionBusy(true)
                setActionError(null)
                try {
                  await api.deleteNode(n.id, n.revision)
                  refreshNetwork()
                  back()
                } catch (e) {
                  setActionError(e instanceof Error ? e.message : 'Could not remove')
                } finally {
                  setActionBusy(false)
                }
              }}
            >
              Remove record
            </Button>
            <Button size="sm" onClick={() => setConfirmRemove(false)}>
              Keep
            </Button>
          </div>
        </div>
      )}
      {actionError && (
        <div className="p-3">
          <Callout tone="danger" title="Not changed">
            {actionError}
          </Callout>
        </div>
      )}
      <Tabs label="Node sections" tabs={tabs} value={tab} onChange={setTab} />
      {tab === 'overview' && <OverviewTab d={d} subscribers={subscribers} outputsUsed={outputsUsed} ports={ports} />}
      {tab === 'ports' && d.splitters.map((s) => <SplitterBlock key={s.id} s={s} />)}
      {tab === 'cores' && <CoresTab d={d} onSplice={(e) => setSplicing(e)} />}
      {tab === 'splices' && <SplicesTab d={d} onChanged={q.reload} onNew={() => setSplicing(null)} />}
      {splicing !== undefined && (
        <SpliceForm
          d={d}
          preset={splicing}
          onClose={() => setSplicing(undefined)}
          onDone={() => {
            setSplicing(undefined)
            setTab('splices')
            q.reload()
          }}
          onStale={q.reload}
        />
      )}
    </PanelShell>
  )
}

function OverviewTab({ d, subscribers, outputsUsed, ports }: { d: NodeDetail; subscribers: number; outputsUsed: number; ports: number }) {
  const [lng, lat] = d.node.location
  const [copied, setCopied] = useState(false)
  const { open } = useApp()
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${lat.toFixed(6)}, ${lng.toFixed(6)}`)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard blocked: the value is visible to copy manually */
    }
  }
  return (
    <>
      <Section title="Location">
        <KeyValue
          rows={[
            [
              'Coordinates',
              <span className="inline-flex items-center gap-1.5">
                {lat.toFixed(6)}, {lng.toFixed(6)}
                <button onClick={copy} className="rounded p-0.5 text-muted hover:text-fg" aria-label="Copy coordinates">
                  <Copy className="h-3.5 w-3.5" aria-hidden />
                </button>
                <span aria-live="polite" className="text-xs text-ok">
                  {copied ? 'Copied' : ''}
                </span>
              </span>,
            ],
            ['Address', d.node.address ?? <span className="text-muted">Not recorded</span>],
            ['Record revision', d.node.revision],
          ]}
        />
      </Section>
      <Section title="Capacity">
        <KeyValue
          rows={[
            ['Cables', d.cables.map((c) => `${c.name} (${c.fiberCount}F)`).join(', ') || '—'],
            ['Splitters', d.splitters.map((s) => `1:${s.ratio}`).join(', ') || '—'],
            ['Splitter outputs', ports ? `${outputsUsed} used of ${ports}` : '—'],
            ...(subscribers ? ([['Subscribers here', subscribers]] as [string, number][]) : []),
            ['Splices', d.connections.length],
          ]}
        />
      </Section>
      {d.olts.map((o) => (
        <Section key={o.id} title={`${o.name} · class ${o.opticClass} · Tx ${o.txPowerDbm} dBm`}>
          <ul className="divide-y divide-border text-sm">
            {o.ports.map((p) => (
              <li key={p.key} className="flex items-center gap-2 py-1.5">
                <span className="num w-16 shrink-0 font-medium">PON {p.ref.kind === 'pon' ? p.ref.port : ''}</span>
                <span className="min-w-0 flex-1 truncate text-muted">{p.connectedTo ?? 'Not patched'}</span>
                {p.connectedTo && p.ref.kind === 'pon' && (
                  <Button size="sm" variant="ghost" onClick={() => open({ kind: 'impact', target: { kind: 'pon', oltId: o.id, port: (p.ref as { port: number }).port } })}>
                    Who is on it?
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Section>
      ))}
    </>
  )
}

function PortTile({ p, s }: { p: PortView; s: SplitterView }) {
  const { open } = useApp()
  const check = (
    <button className="mt-1 text-xs font-medium text-accent underline" onClick={() => open({ kind: 'port-path', splitterId: s.id, port: p.port, nodeId: s.nodeId })}>
      Check path to OLT
    </button>
  )
  if (p.state === 'free') {
    return (
      <div className="rounded-lg border border-dashed border-border p-2 text-sm text-muted">
        <div className="num font-semibold text-fg">Port {p.port}</div>
        Free
        {check}
      </div>
    )
  }
  if (p.state === 'spliced') {
    return (
      <div className="rounded-lg border border-border bg-surface-2 p-2 text-sm">
        <div className="num font-semibold">Port {p.port}</div>
        <div className="break-words text-muted">→ {p.to}</div>
        {check}
      </div>
    )
  }
  const v = budgetVerdict(p.budget)
  const rx = p.service.onu?.rxPowerDbm
  return (
    <button
      onClick={() => open({ kind: 'trace', serviceId: p.service.id })}
      className={cx(
        'rounded-lg border p-2 text-left text-sm hover:bg-surface-2',
        v.tone === 'danger' ? 'border-danger' : v.tone === 'warn' ? 'border-warn' : 'border-border',
      )}
      aria-label={`Port ${p.port}, ${p.service.label}, ${v.label}. Open trace.`}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="num font-semibold">Port {p.port}</span>
        <Badge tone={v.tone} className="!px-1">
          <span className="sr-only">{v.label}</span>
        </Badge>
      </div>
      <div className="truncate">{p.service.label}</div>
      <div className="num truncate text-xs text-muted">{rx !== undefined ? `${fmtDbm(rx)} · ${fmtAge(p.service.onu?.readAt)}` : 'No Rx reading'}</div>
    </button>
  )
}

function SplitterBlock({ s }: { s: SplitterView }) {
  const used = s.ports.filter((p) => p.state !== 'free').length
  return (
    <Section title={`${s.name} · ${used}/${s.ratio} used`}>
      {!s.inputConnected && (
        <div className="mb-2">
          <Callout tone="danger" title="Splitter input is not spliced">
            Subscribers on this splitter cannot be traced to an OLT. Record the incoming splice on the Splices tab.
          </Callout>
        </div>
      )}
      {s.inputFrom && <p className="mb-2 text-xs text-muted">Fed from {s.inputFrom}</p>}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-2">
        {s.ports.map((p) => (
          <PortTile key={p.port} p={p} s={s} />
        ))}
      </div>
    </Section>
  )
}

function CoresTab({ d, onSplice }: { d: NodeDetail; onSplice: (e: EndpointRef) => void }) {
  const { open, canEdit } = useApp()
  const [showAll, setShowAll] = useState<Record<string, boolean>>({})
  if (!d.cables.length) return <Empty title="No cables terminate here" />
  return (
    <>
      {d.cables.map((c) => {
        const used = c.cores.filter((x) => x.endpoint.connectionId).length
        const rows = showAll[c.id] ? c.cores : c.cores.filter((x, i) => x.endpoint.connectionId || i < 12)
        return (
          <Section
            key={c.id}
            title={`${c.name} · ${c.fiberCount}F · ${c.end} end`}
            action={
              <Button size="sm" variant="ghost" onClick={() => open({ kind: 'cable', id: c.id })}>
                Cable details
              </Button>
            }
          >
            <p className="mb-2 text-xs text-muted">
              {c.tier} to {c.otherNodeName} · {used} of {c.fiberCount} cores spliced here
            </p>
            <ul className="divide-y divide-border">
              {rows.map((x) => (
                <li key={x.core} className="flex items-center gap-2 py-1.5 text-sm">
                  <FiberSwatch tube={x.identity.tubeColor.hex} fiber={x.identity.fiberColor.hex} title={x.identity.label} />
                  <span className="num w-10 shrink-0 font-medium">#{x.core}</span>
                  <span className="w-36 shrink-0 truncate text-xs text-muted">{x.identity.label}</span>
                  <span className="min-w-0 flex-1 truncate">{x.endpoint.connectedTo ? `→ ${x.endpoint.connectedTo}` : <span className="text-muted">free</span>}</span>
                  {canEdit && !x.endpoint.connectionId && (
                    <Button size="sm" variant="ghost" onClick={() => onSplice(x.endpoint.ref)} aria-label={`Splice core ${x.core} of ${c.name}`}>
                      <Scissors className="h-4 w-4" aria-hidden />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
            {rows.length < c.cores.length && (
              <Button size="sm" variant="ghost" className="mt-1" onClick={() => setShowAll((s) => ({ ...s, [c.id]: true }))}>
                Show all {c.fiberCount} cores
              </Button>
            )}
          </Section>
        )
      })}
    </>
  )
}

function SplicesTab({ d, onChanged, onNew }: { d: NodeDetail; onChanged: () => void; onNew: () => void }) {
  const { canEdit, refreshNetwork } = useApp()
  const [confirm, setConfirm] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const remove = async (connId: string) => {
    setBusy(true)
    setError(null)
    try {
      await api.removeConnection(connId, d.node.revision)
      setConfirm(null)
      onChanged()
      refreshNetwork()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove the splice')
      if (e instanceof ApiError && e.status === 409) onChanged()
    } finally {
      setBusy(false)
    }
  }
  const byTray = useMemo(() => [...d.connections].sort((a, b) => (a.tray ?? 0) - (b.tray ?? 0) || a.a.localeCompare(b.a)), [d.connections])
  return (
    <Section
      title={`${d.connections.length} recorded`}
      action={
        canEdit ? (
          <Button size="sm" variant="primary" onClick={onNew}>
            <Plus className="h-4 w-4" aria-hidden /> New splice
          </Button>
        ) : undefined
      }
    >
      {error && (
        <div className="mb-2">
          <Callout tone="danger" title="Not removed">
            {error}
          </Callout>
        </div>
      )}
      {!byTray.length && <Empty title="No splices recorded at this node" />}
      <ul className="space-y-2">
        {byTray.map((c) => (
          <li key={c.id} className="rounded-lg border border-border p-2 text-sm">
            <div className="flex items-center gap-2">
              <Badge tone="neutral" icon={false}>
                {c.kind}
              </Badge>
              {c.tray && <span className="text-xs text-muted">Tray {c.tray}</span>}
              {c.lossDb !== null && <span className="num text-xs text-muted">{c.lossDb} dB measured</span>}
              <span className="flex-1" />
              {canEdit && confirm !== c.id && (
                <Button size="sm" variant="ghost" onClick={() => setConfirm(c.id)} aria-label={`Remove splice ${c.a} to ${c.b}`}>
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              )}
            </div>
            <div className="mt-1 break-words">{c.a}</div>
            <div className="break-words text-muted">↔ {c.b}</div>
            <div className="mt-1 text-xs text-muted">
              by {c.createdBy} · {new Date(c.createdAt).toLocaleString()}
            </div>
            {confirm === c.id && (
              <div className="mt-2 rounded-lg bg-danger-soft p-2">
                <p className="text-sm">
                  Remove this splice record from <strong>{d.node.name}</strong>? Any subscriber behind it will show as not traceable until it is re-recorded. This changes the
                  record only, not the physical fibre.
                </p>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="danger" busy={busy} onClick={() => remove(c.id)}>
                    Remove record
                  </Button>
                  <Button size="sm" onClick={() => setConfirm(null)}>
                    Keep
                  </Button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Section>
  )
}

interface Option {
  key: string
  ref: EndpointRef
  label: string
  group: string
}

function freeEndpoints(d: NodeDetail): Option[] {
  const out: Option[] = []
  const add = (group: string, e: EndpointView) => {
    if (!e.connectionId && !e.servesSubscriber) out.push({ key: e.key, ref: e.ref, label: e.label, group })
  }
  for (const o of d.olts) o.ports.forEach((p) => add(`${o.name} PON ports`, p))
  for (const s of d.splitters) {
    add(s.name, s.inputEndpoint)
    for (const p of s.ports) {
      if (p.state === 'free') {
        const ref: EndpointRef = { kind: 'splitter-out', splitterId: s.id, port: p.port }
        out.push({ key: `spl-out:${s.id}:${p.port}`, ref, label: `${s.name} output ${p.port}`, group: s.name })
      }
    }
  }
  for (const c of d.cables) {
    for (const x of c.cores) {
      if (!x.endpoint.connectionId) out.push({ key: x.endpoint.key, ref: x.endpoint.ref, label: `Core ${x.core} · ${x.identity.label}`, group: `${c.name} (${c.fiberCount}F, to ${c.otherNodeName})` })
    }
  }
  return out
}

function SpliceForm({ d, preset, onClose, onDone, onStale }: { d: NodeDetail; preset: EndpointRef | null; onClose: () => void; onDone: () => void; onStale: () => void }) {
  const { refreshNetwork } = useApp()
  const options = useMemo(() => freeEndpoints(d), [d])
  const presetKey = preset ? options.find((o) => JSON.stringify(o.ref) === JSON.stringify(preset))?.key ?? '' : ''
  const [a, setA] = useState(presetKey)
  const [b, setB] = useState('')
  const [kind, setKind] = useState<'fusion' | 'connector' | 'patch'>('fusion')
  const [tray, setTray] = useState('')
  const [loss, setLoss] = useState('')
  const [step, setStep] = useState<'edit' | 'confirm'>('edit')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<{ message: string; stale: boolean } | null>(null)
  const root = useRef<HTMLDivElement>(null)
  useEffect(() => {
    root.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    root.current?.querySelector<HTMLSelectElement>(presetKey ? '#splice-b' : '#splice-a')?.focus({ preventScroll: true })
    // Only when the form opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const optA = options.find((o) => o.key === a)
  const optB = options.find((o) => o.key === b)
  const groups = [...new Set(options.map((o) => o.group))]
  const trayNum = tray === '' ? undefined : Number(tray)
  const lossNum = loss === '' ? undefined : Number(loss)
  const fieldErrors = {
    b: a && b && a === b ? 'Choose two different endpoints' : '',
    tray: trayNum !== undefined && !(Number.isInteger(trayNum) && trayNum >= 1) ? 'Tray must be a whole number from 1' : '',
    loss: lossNum !== undefined && !(lossNum >= 0 && lossNum <= 3) ? 'Loss must be between 0 and 3 dB' : '',
  }
  const valid = optA && optB && !fieldErrors.b && !fieldErrors.tray && !fieldErrors.loss

  const submit = async () => {
    if (!optA || !optB) return
    setBusy(true)
    setError(null)
    try {
      await api.addConnection({ nodeId: d.node.id, expectedRevision: d.node.revision, kind, a: optA.ref, b: optB.ref, tray: trayNum, lossDb: lossNum })
      refreshNetwork()
      onDone()
    } catch (e) {
      const stale = e instanceof ApiError && e.status === 409
      setError({ message: e instanceof Error ? e.message : 'Could not save', stale })
      setStep('edit')
    } finally {
      setBusy(false)
    }
  }

  const select = (id: string, label: string, value: string, set: (v: string) => void, err?: string) => (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => set(e.target.value)}
        aria-invalid={Boolean(err)}
        aria-describedby={err ? `${id}-err` : undefined}
        className="h-10 w-full rounded-lg border border-border bg-surface px-2 text-sm"
      >
        <option value="">Choose a free endpoint…</option>
        {groups.map((g) => (
          <optgroup key={g} label={g}>
            {options
              .filter((o) => o.group === g)
              .map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
          </optgroup>
        ))}
      </select>
      {err && (
        <p id={`${id}-err`} className="mt-1 text-xs text-danger">
          {err}
        </p>
      )}
    </div>
  )

  return (
    <div ref={root} className="scroll-mt-2 border-t-4 border-accent bg-surface-2">
      <Section title={`Record a splice at ${d.node.name}`}>
        {error && (
          <div className="mb-3">
            <Callout tone="danger" title={error.stale ? 'Someone else changed this node' : 'Not saved'}>
              {error.message}
              {error.stale && (
                <div className="mt-2">
                  <Button size="sm" onClick={onStale}>
                    Reload node
                  </Button>
                </div>
              )}
            </Callout>
          </div>
        )}
        {step === 'edit' ? (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault()
              if (valid) setStep('confirm')
            }}
          >
            {select('splice-a', 'From', a, setA)}
            {select('splice-b', 'To', b, setB, fieldErrors.b)}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label htmlFor="splice-kind" className="mb-1 block text-sm font-medium">
                  Type
                </label>
                <select id="splice-kind" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)} className="h-10 w-full rounded-lg border border-border bg-surface px-2 text-sm">
                  <option value="fusion">Fusion</option>
                  <option value="connector">Connector</option>
                  <option value="patch">Patch</option>
                </select>
              </div>
              <div>
                <label htmlFor="splice-tray" className="mb-1 block text-sm font-medium">
                  Tray
                </label>
                <input id="splice-tray" inputMode="numeric" value={tray} onChange={(e) => setTray(e.target.value)} aria-invalid={Boolean(fieldErrors.tray)} className="h-10 w-full rounded-lg border border-border bg-surface px-2 text-sm" />
              </div>
              <div>
                <label htmlFor="splice-loss" className="mb-1 block text-sm font-medium">
                  Loss dB
                </label>
                <input id="splice-loss" inputMode="decimal" placeholder="default" value={loss} onChange={(e) => setLoss(e.target.value)} aria-invalid={Boolean(fieldErrors.loss)} className="h-10 w-full rounded-lg border border-border bg-surface px-2 text-sm" />
              </div>
            </div>
            {(fieldErrors.tray || fieldErrors.loss) && <p className="text-xs text-danger">{fieldErrors.tray || fieldErrors.loss}</p>}
            <div className="flex gap-2">
              <Button type="submit" variant="primary" disabled={!valid}>
                Review
              </Button>
              <Button type="button" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-3">
            <p className="text-sm">You are recording this {kind} splice{trayNum ? ` in tray ${trayNum}` : ''}:</p>
            <div className="rounded-lg border border-border bg-surface p-2 text-sm">
              <div className="font-medium">{optA?.group}</div>
              <div>{optA?.label}</div>
              <div className="my-1 text-muted">↔</div>
              <div className="font-medium">{optB?.group}</div>
              <div>{optB?.label}</div>
            </div>
            <p className="text-xs text-muted">The record is saved with your name and time in the audit log. It changes records only; nothing is sent to network equipment.</p>
            <div className="flex gap-2">
              <Button variant="primary" busy={busy} onClick={submit}>
                Save splice
              </Button>
              <Button onClick={() => setStep('edit')}>Back</Button>
            </div>
          </div>
        )}
      </Section>
    </div>
  )
}
